import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Shield,
  AlertTriangle,
  TrendingUp,
  Search,
  ChevronRight,
  RefreshCw,
  BarChart3,
  AlertCircle,
  CheckCircle,
  Clock,
  Briefcase,
  Download
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const RISK_COLORS = {
  CRITICO: '#ef4444',
  ALTO: '#f97316',
  MEDIO: '#eab308',
  BAJO: '#22c55e',
  MUY_BAJO: '#3b82f6',
  SIN_DATOS: '#94a3b8'
};

const RISK_LABELS = {
  CRITICO: 'Crítico',
  ALTO: 'Alto',
  MEDIO: 'Medio',
  BAJO: 'Bajo',
  MUY_BAJO: 'Muy Bajo',
  SIN_DATOS: 'Sin Datos'
};

export const RiskScoring = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ranking, setRanking] = useState([]);
  const [distribution, setDistribution] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [minScore, setMinScore] = useState('0');
  const [limit, setLimit] = useState('50');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rankingRes, distributionRes] = await Promise.all([
        api.get('/risk/ranking', { 
          params: { limit: parseInt(limit), min_score: parseInt(minScore) } 
        }),
        api.get('/risk/distribution')
      ]);
      
      setRanking(rankingRes.data.ranking);
      setDistribution(distributionRes.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar datos de riesgo');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadData();
  };

  const handleCreateCase = async (item) => {
    try {
      const res = await api.post(`/risk-scoring/clients/${item.cuenta_id}/create-case`);
      toast.success(`Caso creado: ${res.data.case_id?.substring(0, 8)}...`);
      navigate(`/m/gestion-de-casos/cases/${res.data.case_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear caso');
    }
  };

  const handleExport = async (item) => {
    try {
      const res = await api.get(`/risk-scoring/clients/${item.cuenta_id}/export`);
      const blob = new Blob([JSON.stringify(res.data.export, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `risk_scoring_${item.cuenta_id}_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Datos exportados correctamente');
    } catch (error) {
      toast.error('Error al exportar datos');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return RISK_COLORS.CRITICO;
    if (score >= 60) return RISK_COLORS.ALTO;
    if (score >= 40) return RISK_COLORS.MEDIO;
    if (score >= 20) return RISK_COLORS.BAJO;
    return RISK_COLORS.MUY_BAJO;
  };

  const getRiskBadge = (nivel) => {
    const color = RISK_COLORS[nivel] || RISK_COLORS.SIN_DATOS;
    const label = RISK_LABELS[nivel] || nivel;
    return (
      <span 
        className="px-2 py-1 rounded-full text-xs font-medium text-white"
        style={{ backgroundColor: color }}
      >
        {label}
      </span>
    );
  };

  // Preparar datos para gráfico de distribución
  const distributionData = Object.entries(distribution)
    .filter(([_, value]) => value > 0)
    .map(([nivel, cantidad]) => ({
      name: RISK_LABELS[nivel] || nivel,
      value: cantidad,
      color: RISK_COLORS[nivel] || RISK_COLORS.SIN_DATOS
    }));

  // Filtrar ranking por búsqueda
  const filteredRanking = ranking.filter(item =>
    item.cuenta_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calcular totales
  const totalCuentas = Object.values(distribution).reduce((a, b) => a + b, 0);
  const cuentasCriticas = (distribution.CRITICO || 0) + (distribution.ALTO || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Calculando scores de riesgo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50" data-testid="risk-scoring-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">
            Scoring de Riesgo
          </h1>
          <p className="text-slate-600 mt-1">
            Análisis de riesgo por cuenta basado en histórico de alertas
          </p>
        </div>
        <div className="flex gap-2 mt-4 lg:mt-0">
          <Button
            onClick={loadData}
            variant="outline"
            style={{ borderColor: '#cbd5e1', color: '#374151' }}
            data-testid="refresh-risk-btn"
          >
            <RefreshCw size={16} className="mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-white border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Shield size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{totalCuentas}</p>
              <p className="text-sm text-slate-500">Cuentas Analizadas</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-xl">
              <AlertTriangle size={24} className="text-red-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-red-600">{cuentasCriticas}</p>
              <p className="text-sm text-slate-500">Riesgo Alto/Crítico</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-xl">
              <TrendingUp size={24} className="text-orange-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">
                {ranking.length > 0 ? ranking[0].score : 0}
              </p>
              <p className="text-sm text-slate-500">Score Máximo</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-xl">
              <CheckCircle size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-green-600">
                {(distribution.BAJO || 0) + (distribution.MUY_BAJO || 0)}
              </p>
              <p className="text-sm text-slate-500">Riesgo Bajo</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Gráfico de distribución */}
        <Card className="p-6 bg-white border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 size={20} className="text-blue-600" />
            Distribución por Nivel
          </h3>
          <div className="h-64">
            {distributionData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={distributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {distributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400">
                No hay datos disponibles
              </div>
            )}
          </div>
        </Card>

        {/* Filtros y búsqueda */}
        <Card className="p-6 bg-white border-slate-200 lg:col-span-2">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Filtros de Búsqueda</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Buscar Cuenta
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="CUIT, ID de cuenta..."
                  className="pl-10"
                  style={{ backgroundColor: '#ffffff' }}
                  data-testid="risk-search-input"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Score Mínimo
              </label>
              <Select value={minScore} onValueChange={setMinScore}>
                <SelectTrigger style={{ backgroundColor: '#ffffff' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Todos</SelectItem>
                  <SelectItem value="20">≥ 20 (Bajo+)</SelectItem>
                  <SelectItem value="40">≥ 40 (Medio+)</SelectItem>
                  <SelectItem value="60">≥ 60 (Alto+)</SelectItem>
                  <SelectItem value="80">≥ 80 (Crítico)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Límite
              </label>
              <Select value={limit} onValueChange={setLimit}>
                <SelectTrigger style={{ backgroundColor: '#ffffff' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25 cuentas</SelectItem>
                  <SelectItem value="50">50 cuentas</SelectItem>
                  <SelectItem value="100">100 cuentas</SelectItem>
                  <SelectItem value="200">200 cuentas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4">
            <Button
              onClick={handleSearch}
              style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
              data-testid="apply-risk-filters-btn"
            >
              <Search size={16} className="mr-2" />
              Aplicar Filtros
            </Button>
          </div>
        </Card>
      </div>

      {/* Tabla de ranking */}
      <Card className="bg-white border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp size={20} className="text-red-600" />
            Ranking de Cuentas por Riesgo
            <span className="text-sm font-normal text-slate-500 ml-2">
              ({filteredRanking.length} resultados)
            </span>
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Pos.
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Cuenta
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Nivel
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Alertas
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Abiertas
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Críticas
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Última Alerta
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredRanking.length > 0 ? (
                filteredRanking.map((item, index) => (
                  <tr key={item.cuenta_id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className={`font-bold ${index < 3 ? 'text-red-600' : 'text-slate-600'}`}>
                        #{index + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        {item.nombre && (
                          <span className="block font-medium text-slate-900 text-sm">
                            {item.nombre}
                          </span>
                        )}
                        <span className="font-mono text-xs text-slate-500">
                          {item.cuenta_id}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div 
                          className="w-12 h-2 rounded-full bg-slate-200 overflow-hidden"
                        >
                          <div 
                            className="h-full rounded-full transition-all"
                            style={{ 
                              width: `${item.score}%`,
                              backgroundColor: getScoreColor(item.score)
                            }}
                          />
                        </div>
                        <span className="font-bold text-slate-900 w-8">
                          {item.score}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {getRiskBadge(item.nivel_riesgo)}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600">
                      {item.total_alertas}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={item.alertas_abiertas > 0 ? 'text-orange-600 font-medium' : 'text-slate-400'}>
                        {item.alertas_abiertas}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={item.alertas_criticas > 0 ? 'text-red-600 font-medium' : 'text-slate-400'}>
                        {item.alertas_criticas}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-slate-500">
                      {item.ultima_alerta 
                        ? new Date(item.ultima_alerta).toLocaleDateString('es-AR')
                        : '-'
                      }
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCreateCase(item)}
                          className="text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                          data-testid={`create-case-${item.cuenta_id}`}
                          title="Generar Caso"
                        >
                          <Briefcase size={15} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleExport(item)}
                          className="text-green-600 hover:text-green-800 hover:bg-green-50"
                          data-testid={`export-risk-${item.cuenta_id}`}
                          title="Exportar Data"
                        >
                          <Download size={15} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/m/empresas/padron-interno/cliente/${item.cuenta_id}`)}
                          className="text-blue-600 hover:text-blue-800"
                          data-testid={`view-account-${item.cuenta_id}`}
                          title="Ver Cliente"
                        >
                          <ChevronRight size={15} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                    No se encontraron cuentas con los filtros aplicados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default RiskScoring;
